import { useMemo, useState } from 'react';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { CreatePersonalMailboxDialog } from '../components/mail/admin/CreatePersonalMailboxDialog';
import { CreateSharedMailboxDialog } from '../components/mail/admin/CreateSharedMailboxDialog';
import { MailboxDetailDialog } from '../components/mail/admin/MailboxDetailDialog';
import { StatusBadge } from '../components/StatusBadge';
import { listAdminCandidates, listAdminMailboxes } from '../lib/mailAdminApi';
import type { MailAdminMailboxDto } from '../lib/mailAdminTypes';
import { mailboxSearchText } from '../lib/mailAdminValidation';
import { useApiResource } from '../lib/useApiResource';

type Dialog = null | { kind: 'personal' } | { kind: 'shared' } | { kind: 'detail'; mailbox: MailAdminMailboxDto };

/**
 * Mailbox administration (spec 008, M4) — ADMIN only. Creates personal/shared mailboxes and
 * manages names, aliases, members and status. Never log or persist mailbox contents here;
 * addresses and member names are personal data.
 */
export function MailAdminPage() {
  const mailboxes = useApiResource(listAdminMailboxes, []);
  const candidates = useApiResource(listAdminCandidates, []);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | 'personal' | 'shared'>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'disabled'>('');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rows = useMemo(() => {
    if (!mailboxes.data) return null;
    const q = search.trim().toLowerCase();
    return mailboxes.data.filter(
      (m) =>
        (!typeFilter || m.type === typeFilter) &&
        (!statusFilter || m.status === statusFilter) &&
        (!q || mailboxSearchText(m).includes(q)),
    );
  }, [mailboxes.data, search, typeFilter, statusFilter]);

  const filtered = !!(search.trim() || typeFilter || statusFilter);
  const candidateList = candidates.data ?? [];

  const created = (message: string) => {
    setDialog(null);
    setNotice(message);
    mailboxes.reload();
    candidates.reload();
  };

  const columns: DataTableColumn<MailAdminMailboxDto>[] = [
    {
      key: 'address',
      header: 'Address',
      value: (m) => m.address,
      render: (m) => <span style={{ overflowWrap: 'anywhere' }}>{m.address}</span>,
    },
    { key: 'name', header: 'Name', value: (m) => m.displayName },
    {
      key: 'type',
      header: 'Type',
      value: (m) => m.type,
      render: (m) => <StatusBadge label={m.type === 'personal' ? 'Personal' : 'Shared'} />,
    },
    {
      key: 'status',
      header: 'Status',
      value: (m) => m.status,
      render: (m) => (
        <StatusBadge
          label={m.status === 'active' ? 'Active' : 'Disabled'}
          tone={m.status === 'active' ? 'good' : 'warn'}
        />
      ),
    },
    { key: 'members', header: 'Members', value: (m) => m.members.length, align: 'right', width: '90px' },
    {
      key: 'aliases',
      header: 'Aliases',
      value: (m) => m.aliases.join(', '),
      render: (m) =>
        m.aliases.length === 0 ? (
          <span className="cell-sub">None</span>
        ) : (
          <span style={{ overflowWrap: 'anywhere' }}>{m.aliases.join(', ')}</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '110px',
      render: (m) => (
        <button
          type="button"
          className="btn btn-secondary"
          aria-label={`Manage ${m.address}`}
          onClick={() => setDialog({ kind: 'detail', mailbox: m })}
        >
          Manage
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Mail administration</h1>
          <p>
            Create personal and shared mailboxes, manage aliases and members, and disable a mailbox
            that should stop receiving mail.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={() => setDialog({ kind: 'personal' })}>
            Create personal mailbox
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setDialog({ kind: 'shared' })}>
            Create shared mailbox
          </button>
        </div>
      </div>

      {notice && (
        <div role="status" className="form-hint" style={{ marginBottom: '1rem' }}>
          {notice}
        </div>
      )}
      {mailboxes.error && (
        <div className="error-banner" role="alert">
          {mailboxes.error}{' '}
          <button type="button" className="btn btn-secondary" onClick={mailboxes.reload}>
            Retry
          </button>
        </div>
      )}
      {candidates.error && (
        <div className="error-banner" role="alert">
          The list of people could not be loaded, so mailboxes cannot be created or have members
          added: {candidates.error}{' '}
          <button type="button" className="btn btn-secondary" onClick={candidates.reload}>
            Retry
          </button>
        </div>
      )}

      <DataTable
        id="mail-admin-mailboxes"
        rows={rows}
        loading={mailboxes.loading}
        error={null}
        controls={false}
        pageSize={25}
        noun="mailboxes"
        empty={mailboxes.error ? 'Mailboxes could not be loaded.' : 'No mailboxes yet.'}
        getRowKey={(m) => m.id}
        columns={columns}
        toolbar={
          <div className="section-toolbar" style={{ margin: 0 }}>
            <input
              type="search"
              value={search}
              placeholder="Search address, name or alias"
              aria-label="Search mailboxes"
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              aria-label="Filter by type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
            >
              <option value="">All types</option>
              <option value="personal">Personal</option>
              <option value="shared">Shared</option>
            </select>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            {filtered && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setSearch('');
                  setTypeFilter('');
                  setStatusFilter('');
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        }
      />

      {dialog?.kind === 'personal' && (
        <CreatePersonalMailboxDialog
          candidates={candidateList}
          onClose={() => setDialog(null)}
          onCreated={() => created('Personal mailbox created.')}
        />
      )}
      {dialog?.kind === 'shared' && (
        <CreateSharedMailboxDialog
          candidates={candidateList}
          onClose={() => setDialog(null)}
          onCreated={() => created('Shared mailbox created.')}
        />
      )}
      {dialog?.kind === 'detail' && (
        <MailboxDetailDialog
          key={dialog.mailbox.id}
          mailbox={dialog.mailbox}
          candidates={candidateList}
          onClose={() => setDialog(null)}
          onChanged={() => {
            mailboxes.reload();
            candidates.reload();
          }}
        />
      )}
    </div>
  );
}
